/**
 * jai1 tasks cancel <id> [-y] [-j]
 */
import { Command } from 'commander';
import chalk from 'chalk';
import { confirm } from '@inquirer/prompts';
import { TaskService } from '../../services/task.service.js';
import { STATUS_ICONS, type TaskCancelOptions, type TaskStatusType } from '../../types/task.types.js';

export function createTaskCancelCommand(): Command {
    return new Command('cancel')
        .description('Cancel a task (with dependency impact check)')
        .argument('<id>', 'Task ID (e.g. T-001)')
        .option('-y, --yes', 'Skip confirmation')
        .option('-j, --json', 'Output JSON')
        .action(async (id: string, options: TaskCancelOptions) => {
            const service = new TaskService();

            try {
                const task = await service.findById(id);
                if (!task) {
                    console.error(chalk.red(`❌ Task ${id} not found`));
                    process.exit(1);
                }

                if (task.status === 'cancelled') {
                    console.error(chalk.yellow(`⚠️  Task ${id} is already cancelled`));
                    process.exit(0);
                }

                if (task.status === 'done') {
                    console.error(chalk.red(`❌ Task ${id} is already done — cannot cancel`));
                    process.exit(1);
                }

                // Check dependents
                const dependents = await service.getDependents(id);
                const activeDependents = dependents.filter(
                    (t) => t.status !== 'done' && t.status !== 'cancelled'
                );

                // Show task info
                const icon = STATUS_ICONS[task.status as TaskStatusType] || '📋';
                console.log(chalk.bold(`\n⚫ Cancel: ${icon} ${task.id} — ${task.title}\n`));

                if (activeDependents.length > 0) {
                    console.log(chalk.yellow(`⚠️  ${activeDependents.length} task(s) phụ thuộc vào ${id}:`));
                    for (const dep of activeDependents) {
                        const depIcon = STATUS_ICONS[dep.status as TaskStatusType] || '📋';
                        console.log(`   ${depIcon} ${chalk.dim(dep.id)} ${dep.title}`);
                    }
                    console.log(
                        chalk.dim(
                            `\n   → Cancel sẽ resolve dependency, các task trên có thể được unblock.`
                        )
                    );
                    console.log();
                }

                // Confirm
                if (!options.yes) {
                    const proceed = await confirm({
                        message: `Cancel task ${id}?`,
                        default: false,
                    });
                    if (!proceed) {
                        console.log(chalk.dim('Đã huỷ.'));
                        return;
                    }
                }

                const updated = await service.cancel(id);

                if (options.json) {
                    console.log(JSON.stringify(updated, null, 2));
                    return;
                }

                console.log(chalk.green(`✅ ${updated.id}: ${updated.title} → cancelled`));
            } catch (error) {
                console.error(
                    chalk.red(`❌ ${error instanceof Error ? error.message : String(error)}`)
                );
                process.exit(1);
            }
        });
}
